import { AppSyncFarm } from 'common/models/farm';
import { Nullable } from 'common/models/optional';
import {
  ContextWithStringAmount,
  GeneralErrorEvent,
  GetStoredTxResponseEvent,
  XStateEvent,
} from 'common/models/xstate';
import { VaultApi } from 'sdk/vaultApi';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import { FarmUtils } from 'utils/farmUtils';
import { InputUtils } from 'utils/inputUtils';
import { NumberUtils } from 'utils/numberUtils';
import { TransactionUtils } from 'utils/transactionUtils';
import { assign, interpret, Machine, spawn } from 'xstate';
import { VaultDepositEvent } from './vaultDepositEvent';
import { XStateUtils } from './xStateUtils';
import { StorageUtils } from 'utils/storageUtils';
import {
  createTxMachine,
  initialTxContext,
  RemoteEventExecutingTx,
  RemoteEventNoStoredTx,
  RemoteEventNoWalletConnected,
  RemoteEventTxError,
  RemoteEventTxSuccess,
  RemoteTxEvent,
} from './txMachine';
import { SwalUtils } from 'utils/swalUtils';
import { ChainId } from 'constants/constants';
import { Address } from 'viem';

export enum VaultDepositState {
  BASE_STATE = 'BASE_STATE',
  CHECK_INPUT = 'CHECK_INPUT',
  LOADING_DATA = 'LOADING_DATA',
  INVALID_INPUT = 'INVALID_INPUT',
  INPUT_TOO_SMALL = 'INPUT_TOO_SMALL',
  TOO_MANY_DECIMALS = 'TOO_MANY_DECIMALS',
  EXCEEDED_MAX_DEPOSIT = 'EXCEEDED_MAX_DEPOSIT',
  INSUFFICIENT_TOKEN = 'INSUFFICIENT_TOKEN',
  CHECKING_FOR_EXISTING_TX = 'CHECKING_FOR_DEPOSIT_TX',
  DEPOSIT_AND_GET_TX = 'DEPOSIT_AND_GET_TX',
  APPROVE_AND_GET_TX = 'APPROVE_AND_GET_TX',
  RESET_AND_GET_TX = 'RESET_AND_GET_TX',
  HANDLE_DEPOSIT_TX = 'HANDLE_DEPOSIT_TX',
  HANDLE_APPROVE_TX = 'HANDLE_APPROVE_TX',
  HANDLE_RESET_TX = 'HANDLE_RESET_TX',
}

type DepositSchema = {
  states: {
    [s in VaultDepositState]: object;
  };
};

enum VaultDepositAction {
  DEPOSIT_ERROR = 'DEPOSIT_ERROR',
  APPROVE_ERROR = 'APPROVE_ERROR',
  RESET_ERROR = 'RESET_ERROR',
}

// TODO: This is duplicated in vaultWithdrawMachine, should consolidate
export enum VaultDepositService {
  GET_VAULT_DATA = 'getVaultData',
}

export type EventInputChangeArgs = {
  amount: string;
  approveExactAmount: boolean;
};

type EventLiquidityPercentageChangeArgs = {
  value: number;
};

export type EventUpdateAccountInfoArgs = {
  address: Nullable<Address>;
  chainId: number;
  farm: AppSyncFarm;
  token: string;
};

type EventDepositArgs = object;

// NOTE that xstate always whats the value to be named 'value', don't change the name,
// The actual value can be anything, but the variable has to be called value
type EventInputChange = XStateEvent<
  VaultDepositEvent.INPUT_CHANGE,
  EventInputChangeArgs
>;
type EventLiquidityPercentageChange = XStateEvent<
  VaultDepositEvent.LIQUIDITY_PERCENTAGE_CHANGE,
  EventLiquidityPercentageChangeArgs
>;
type EventUpdateAccountInfo = XStateEvent<
  VaultDepositEvent.UPDATE_ACCOUNT_INFO,
  EventUpdateAccountInfoArgs
>;
type EventCheckInput = XStateEvent<VaultDepositEvent.CHECK_INPUT>;
type EventDeposit = XStateEvent<VaultDepositEvent.DEPOSIT, EventDepositArgs>;
type EventApproveTokenAllowance =
  XStateEvent<VaultDepositEvent.APPROVE_TOKEN_ALLOWANCE>;
type EventResetTokenAllowance =
  XStateEvent<VaultDepositEvent.RESET_TOKEN_ALLOWANCE>;
type EventLoadData = XStateEvent<VaultDepositEvent.LOAD_DATA>;

type VaultDepositEvents =
  | EventInputChange
  | EventLiquidityPercentageChange
  | EventUpdateAccountInfo
  | EventCheckInput
  | EventDeposit
  | EventApproveTokenAllowance
  | EventResetTokenAllowance
  | EventLoadData
  // Remote Tx Events
  | RemoteEventExecutingTx
  | RemoteEventTxError
  | RemoteEventTxSuccess
  | RemoteEventNoWalletConnected
  | RemoteEventNoStoredTx;

export type VaultDepositContext = {
  accountAddress: Nullable<Address>;
  chainId: Nullable<number>;
  amount: string;
  amountUsd: number;
  inputError?: string;
  liquidityPercentage: number;
  tokenPrice: number;
  tokenMaxDeposit: bigint;
  storedTx: Nullable<StorageUtils.StoredTx>;
  farm: Nullable<AppSyncFarm>;
  gasLimit: bigint;
  depositError: Nullable<Error>;
  dataResponse: Nullable<VaultApi.GetVaultDataResponse>;
  dataError: Nullable<Error>;
  isDepositing: boolean;
  isLoadingData: boolean;
  isTokenApproved: Nullable<boolean>;
  isApprovingToken: boolean;
  approveExactAmount: boolean;
  tokenAllowance: bigint;
  isResettingToken: boolean;
  // only used for wETH vault. Can be either ETH or wETH
  token: string;
  // User info
  availableToken: bigint;
  availableTokenUsd?: string;
  txRef: any;
} & ContextWithStringAmount;

export const initialDepositContext: VaultDepositContext = {
  accountAddress: null,
  chainId: null,
  amount: '0',
  amountUsd: 0,
  inputError: undefined,
  liquidityPercentage: 0,
  tokenPrice: 1,
  tokenMaxDeposit: 0n,
  storedTx: null,
  farm: null,
  gasLimit: CryptoUtils.calculateGasMargin(
    BigInt(GAS_SETTINGS.deposit),
    ChainId.Mainnet
  ),
  depositError: null,
  dataResponse: null,
  dataError: null,
  isDepositing: false,
  isLoadingData: false,
  isTokenApproved: null,
  isApprovingToken: false,
  approveExactAmount: false,
  tokenAllowance: 0n,
  isResettingToken: false,
  token: 'eth',
  // User info
  availableToken: 0n,
  availableTokenUsd: '0',
  txRef: undefined,
};

const isInsufficientToken = (context: VaultDepositContext) => {
  // If we haven't fetched the farm data yet, don't give a false positive
  if (context.dataResponse == null || context.farm == null) {
    return false;
  }
  return (
    context.dataResponse.tokenBalance <
    NumberUtils.stringToBN(
      context.amount,
      BigInt(FarmUtils.getBaseTokenDecimals(context.farm))
    )
  );
};

const isAboveMaxDepositAmount = (context: VaultDepositContext) => {
  // If we haven't fetched the farm data yet, don't give a false positive
  if (context.dataResponse == null || context.farm == null) {
    return false;
  }
  return (
    NumberUtils.stringToBN(
      context.amount,
      BigInt(FarmUtils.getBaseTokenDecimals(context.farm))
    ) > context.dataResponse.tokenMaxDeposit
  );
};

type GetVaultDataResponseEvent = {
  type: string;
  data: VaultApi.GetVaultDataResponse;
};

const vaultApi = VaultApi.Vault.getInstance();

// https://github.com/ichifarm/ichi-app/blob/master/src/pages/Farm/index.tsx#L488
export const vaultDepositMachine = Machine<
  VaultDepositContext,
  DepositSchema,
  VaultDepositEvents
>(
  {
    key: 'vaultDeposit',
    id: 'vaultDeposit',
    initial: VaultDepositState.BASE_STATE,
    context: XStateUtils.shortStringify(initialDepositContext),
    states: {
      [VaultDepositState.BASE_STATE]: {
        on: {
          [VaultDepositEvent.INPUT_CHANGE]: {
            target: VaultDepositState.CHECK_INPUT,
            actions: assign({
              amount: (_, event) => event.value.amount,
              approveExactAmount: (_, event) => event.value.approveExactAmount,
            }),
          },
          [VaultDepositEvent.UPDATE_ACCOUNT_INFO]: {
            target: VaultDepositState.LOADING_DATA,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
              farm: (_, event: EventUpdateAccountInfo) => event.value.farm,
              tokenPrice: (_, event: EventUpdateAccountInfo) =>
                FarmUtils.getBaseToken(event.value.farm).price,
              token: (_, event: EventUpdateAccountInfo) => event.value.token,
            }),
          },
          [VaultDepositEvent.DEPOSIT]: {
            target: VaultDepositState.DEPOSIT_AND_GET_TX,
          },
          [VaultDepositEvent.APPROVE_TOKEN_ALLOWANCE]: {
            target: VaultDepositState.APPROVE_AND_GET_TX,
          },
          [VaultDepositEvent.RESET_TOKEN_ALLOWANCE]: {
            target: VaultDepositState.RESET_AND_GET_TX,
          },
          [VaultDepositEvent.LOAD_DATA]: {
            target: VaultDepositState.LOADING_DATA,
          },
        },
      },
      [VaultDepositState.CHECKING_FOR_EXISTING_TX]: {
        on: {
          [VaultDepositEvent.UPDATE_ACCOUNT_INFO]: {
            target: VaultDepositState.LOADING_DATA,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
              farm: (_, event: EventUpdateAccountInfo) => event.value.farm,
              tokenPrice: (_, event: EventUpdateAccountInfo) =>
                FarmUtils.getBaseToken(event.value.farm).price,
              token: (_, event: EventUpdateAccountInfo) => event.value.token,
            }),
          },
          [VaultDepositEvent.INPUT_CHANGE]: {
            target: VaultDepositState.CHECK_INPUT,
            actions: assign({
              amount: (_, event) => event.value.amount,
              approveExactAmount: (_, event) => event.value.approveExactAmount,
            }),
          },
        },
        invoke: {
          src: (context) => {
            return TransactionUtils.getStoredTx({
              accountAddress: context.accountAddress as string,
              chainId: context.chainId as number,
              functionName: VaultApi.Vault.depositFN,
            });
          },
          onDone: {
            target: VaultDepositState.HANDLE_DEPOSIT_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          // Error just means no tx found, go back to waiting for input
          onError: {
            target: VaultDepositState.CHECK_INPUT,
          },
        },
      },
      [VaultDepositState.DEPOSIT_AND_GET_TX]: {
        entry: assign({
          isDepositing: () => true,
        }),
        exit: assign({
          isDepositing: () => false,
        }),
        invoke: {
          src: (context) => {
            return vaultApi.deposit({
              chainId: context.chainId as number,
              farm: context.farm as AppSyncFarm,
              accountAddress: context.accountAddress as Address,
              amount: context.amount,
              amountUsd: context.amountUsd,
              token: context.token,
            });
          },
          onDone: {
            // Here we aren't going to indicate success as this is 'detached' async response where
            // we have to wait for the actual tx to finish, which is being tracked in localstorage/indexDB
            // See the activities below
            target: VaultDepositState.HANDLE_DEPOSIT_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          onError: {
            target: VaultDepositState.BASE_STATE,
            actions: [
              VaultDepositAction.DEPOSIT_ERROR,
              assign({
                depositError: (_, event: GeneralErrorEvent) => event.data,
                isDepositing: () => false,
              }),
            ],
          },
        },
      },
      [VaultDepositState.HANDLE_DEPOSIT_TX]: {
        entry: assign({
          isDepositing: () => true,

          txRef: (context) => {
            const c = context as unknown as VaultDepositContext;
            return spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: c.chainId,
                accountAddress: c.accountAddress,
                storedTx: c.storedTx,
              }),
              {
                sync: true,
                name: `tx-machine-${
                  (context as unknown as VaultDepositContext).storedTx?.hash
                }`,
              }
            );
          },
        }),
        exit: assign({
          isDepositing: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: VaultDepositState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: VaultDepositState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: VaultDepositState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: [
              RemoteTxEvent.REMOTE_TX_SUCCESS,
              assign({
                amount: () => '0',
              }),
            ],
            target: VaultDepositState.LOADING_DATA,
          },
        },
      },
      [VaultDepositState.RESET_AND_GET_TX]: {
        entry: assign({
          isApprovingToken: () => true,
        }),
        exit: assign({
          isApprovingToken: () => false,
        }),
        invoke: {
          src: (context) => {
            return vaultApi.resetTokenAllowance({
              amount: context.amount,
              approveExactAmount: context.approveExactAmount,
              accountAddress: context.accountAddress as string,
              chainId: context.chainId as number,
              farm: context.farm as AppSyncFarm,
            });
          },
          onDone: {
            target: VaultDepositState.HANDLE_RESET_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          onError: {
            target: VaultDepositState.BASE_STATE,
            actions: [
              VaultDepositAction.RESET_ERROR,
              assign({
                isTokenApproved: () => false,
              }),
            ],
          },
        },
      },
      [VaultDepositState.HANDLE_RESET_TX]: {
        entry: assign({
          isApprovingToken: () => true,
          txRef: (context) => {
            // Can't cast above, not sure why atm
            const c = context as unknown as VaultDepositContext;
            return spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: c.chainId,
                accountAddress: c.accountAddress,
                storedTx: c.storedTx,
              }),
              { sync: true, name: `tx-machine-${c.storedTx?.hash}` }
            );
          },
        }),
        exit: assign({
          isApprovingToken: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: VaultDepositState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: VaultDepositState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: VaultDepositState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: [RemoteTxEvent.REMOTE_TX_SUCCESS],
            target: VaultDepositState.LOADING_DATA,
          },
        },
      },
      [VaultDepositState.APPROVE_AND_GET_TX]: {
        entry: assign({
          isApprovingToken: () => true,
        }),
        exit: assign({
          isApprovingToken: () => false,
        }),
        invoke: {
          src: (context) => {
            return vaultApi.approveTokenAllowance({
              amount: context.amount,
              approveExactAmount: context.approveExactAmount,
              accountAddress: context.accountAddress as string,
              chainId: context.chainId as number,
              farm: context.farm as AppSyncFarm,
            });
          },
          onDone: {
            target: VaultDepositState.HANDLE_APPROVE_TX,
            actions: assign({
              storedTx: (_, event: GetStoredTxResponseEvent) => event.data,
            }),
          },
          onError: {
            target: VaultDepositState.BASE_STATE,
            actions: [VaultDepositAction.APPROVE_ERROR],
          },
        },
      },
      [VaultDepositState.HANDLE_APPROVE_TX]: {
        entry: assign({
          isApprovingToken: () => true,
          txRef: (context) => {
            // Can't cast above, not sure why atm
            const c = context as unknown as VaultDepositContext;
            return spawn(
              createTxMachine('txMachine', {
                ...initialTxContext,
                chainId: c.chainId,
                accountAddress: c.accountAddress,
                storedTx: c.storedTx,
              }),
              { sync: true, name: `tx-machine-${c.storedTx?.hash}` }
            );
          },
        }),
        exit: assign({
          isApprovingToken: () => false,
        }),
        // These assume the txMachine handles the notifications
        on: {
          [RemoteTxEvent.REMOTE_EXECUTING_TX]: {},
          [RemoteTxEvent.REMOTE_NO_STORED_TX]: {
            target: VaultDepositState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_NO_WALLET_CONNECTED]: {
            target: VaultDepositState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_ERROR]: {
            target: VaultDepositState.BASE_STATE,
          },
          [RemoteTxEvent.REMOTE_TX_SUCCESS]: {
            // re-'call' the action so the react component can listen and react
            actions: [RemoteTxEvent.REMOTE_TX_SUCCESS],
            target: VaultDepositState.LOADING_DATA,
          },
        },
      },
      [VaultDepositState.LOADING_DATA]: {
        entry: assign({
          isLoadingData: () => true,
          dataError: () => null,
        }),
        exit: assign({
          isLoadingData: () => false,
        }),
        invoke: {
          src: VaultDepositService.GET_VAULT_DATA,
          onDone: {
            target: VaultDepositState.CHECKING_FOR_EXISTING_TX,
            actions: assign({
              dataResponse: (_, event: GetVaultDataResponseEvent) => event.data,
              isTokenApproved: (_, event: GetVaultDataResponseEvent) =>
                event.data.isTokenApproved,
              tokenMaxDeposit: (_, event: GetVaultDataResponseEvent) =>
                event.data.tokenMaxDeposit,
              tokenAllowance: (_, event: GetVaultDataResponseEvent) =>
                event.data.tokenAllowance,
              amount: (context) => context.amount,
              amountUsd: (context) => context.amountUsd,
              availableToken: (_, event) => event.data.tokenBalance,
              availableTokenUsd: (_, event) =>
                NumberUtils.BNtoStringWithoutDecimals(
                  event.data.tokenBalance,
                  event.data.tokenDecimals
                ),
            }),
          },
          onError: {
            target: VaultDepositState.CHECK_INPUT,
            actions: assign({
              dataError: (_, event: GeneralErrorEvent) => event.data,
            }),
          },
        },
      },
      [VaultDepositState.CHECK_INPUT]: {
        always: [
          {
            target: VaultDepositState.BASE_STATE,
            cond: InputUtils.isDefaultInput,
          },
          {
            target: VaultDepositState.BASE_STATE,
            cond: InputUtils.isZeroInput,
          },
          {
            target: VaultDepositState.INPUT_TOO_SMALL,
            cond: InputUtils.isVaultInputWithPriceTooSmall,
          },
          {
            target: VaultDepositState.TOO_MANY_DECIMALS,
            cond: InputUtils.isTooManyDecimals18,
          },
          {
            target: VaultDepositState.INSUFFICIENT_TOKEN,
            cond: isInsufficientToken,
          },
          {
            target: VaultDepositState.EXCEEDED_MAX_DEPOSIT,
            cond: isAboveMaxDepositAmount,
          },
          {
            target: VaultDepositState.INVALID_INPUT,
            cond: InputUtils.isInvalidInput,
          },
          {
            target: VaultDepositState.BASE_STATE,
            cond: InputUtils.isValidInput,
          },
        ],
      },
      [VaultDepositState.INVALID_INPUT]: {
        entry: assign({
          inputError: () => 'Invalid Input!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [VaultDepositEvent.INPUT_CHANGE]: {
            target: VaultDepositState.CHECK_INPUT,
            actions: assign({
              amount: (_, event) => event.value.amount,
              approveExactAmount: (_, event) => event.value.approveExactAmount,
            }),
          },
        },
      },
      [VaultDepositState.INPUT_TOO_SMALL]: {
        entry: assign({
          inputError: () => 'Amount is too small!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [VaultDepositEvent.INPUT_CHANGE]: {
            target: VaultDepositState.CHECK_INPUT,
            actions: assign({
              amount: (_, event) => event.value.amount,
              approveExactAmount: (_, event) => event.value.approveExactAmount,
            }),
          },
        },
      },
      [VaultDepositState.TOO_MANY_DECIMALS]: {
        entry: assign({
          inputError: () => 'Too many decimals!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [VaultDepositEvent.INPUT_CHANGE]: {
            target: VaultDepositState.CHECK_INPUT,
            actions: assign({
              amount: (_, event) => event.value.amount,
              approveExactAmount: (_, event) => event.value.approveExactAmount,
            }),
          },
        },
      },
      [VaultDepositState.EXCEEDED_MAX_DEPOSIT]: {
        entry: assign({
          inputError: () => 'Exceeded MAX deposit amount!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [VaultDepositEvent.INPUT_CHANGE]: {
            target: VaultDepositState.CHECK_INPUT,
            actions: assign({
              amount: (_, event) => event.value.amount,
              approveExactAmount: (_, event) => event.value.approveExactAmount,
            }),
          },
        },
      },
      [VaultDepositState.INSUFFICIENT_TOKEN]: {
        entry: assign({
          inputError: () => 'Insufficient Token!',
        }),
        exit: assign({
          inputError: () => undefined,
        }),
        on: {
          [VaultDepositEvent.INPUT_CHANGE]: {
            target: VaultDepositState.CHECK_INPUT,
            actions: assign({
              amount: (_, event) => event.value.amount,
              approveExactAmount: (_, event) => event.value.approveExactAmount,
            }),
          },
          [VaultDepositEvent.UPDATE_ACCOUNT_INFO]: {
            target: VaultDepositState.LOADING_DATA,
            actions: assign({
              accountAddress: (_, event: EventUpdateAccountInfo) =>
                event.value.address,
              chainId: (_, event: EventUpdateAccountInfo) =>
                event.value.chainId,
              farm: (_, event: EventUpdateAccountInfo) => event.value.farm,
              tokenPrice: (_, event: EventUpdateAccountInfo) =>
                FarmUtils.getBaseToken(event.value.farm).price,
              token: (_, event: EventUpdateAccountInfo) => event.value.token,
            }),
          },
        },
      },
    },
  },
  {
    services: {
      [VaultDepositService.GET_VAULT_DATA]: (context) => {
        return vaultApi.getVaultData({
          // availableToken: context.availableToken,
          amount: context.amount,
          accountAddress: context.accountAddress as Address,
          chainId: context.chainId as number,
          farm: context.farm as AppSyncFarm,
          token: context.token,
        });
      },
    },
    actions: {
      [VaultDepositAction.DEPOSIT_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ??
            evt?.data?.message ??
            'Error Depositing!',
        });
      },
      [VaultDepositAction.APPROVE_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Approving!',
        });
      },
      [VaultDepositAction.RESET_ERROR]: (_, event) => {
        // I wonder if there is a better way to do this
        const evt = event as unknown as GeneralErrorEvent;
        void SwalUtils.ToastErrorTx.fire({
          title:
            evt?.data?.shortMessage ?? evt?.data?.message ?? 'Error Resetting!',
        });
      },
    },
  }
);

export const vaultDepositService = interpret(vaultDepositMachine, {
  // devTools: process.env.NODE_ENV === 'development'
}).start();

vaultDepositService.subscribe((s) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[vaultDepositService:debug]', s);
  }
});
